/**
 * ClinicalAIDashboard.jsx
 * AI-powered clinical analysis and acuity trends dashboard
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import {
  Brain, TrendingUp, TrendingDown, Activity, AlertTriangle,
  Users, Heart, Loader2, RefreshCw, BarChart3, Target,
  Zap, Eye, ChevronRight, AlertOctagon
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClinicalAIDashboard() {
  const [loading, setLoading] = useState(true);
  const [aiStatus, setAiStatus] = useState(null);
  const [acuityTrends, setAcuityTrends] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientAnalysis, setPatientAnalysis] = useState(null);
  const [analyzingPatient, setAnalyzingPatient] = useState(false);
  const [timeRange, setTimeRange] = useState('30');
  
  useEffect(() => {
    loadDashboard();
  }, [timeRange]);
  
  const loadDashboard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hospice_token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [statusRes, trendsRes] = await Promise.all([
        fetch(`${API_URL}/api/clinical-features/ai-agent/status`, { headers }),
        fetch(`${API_URL}/api/clinical-features/acuity/trends?days=${timeRange}`, { headers })
      ]);
      
      if (statusRes.ok) setAiStatus(await statusRes.json());
      if (trendsRes.ok) setAcuityTrends(await trendsRes.json());
      
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load AI dashboard');
    } finally {
      setLoading(false);
    }
  };
  
  const analyzePatient = async (patientId) => {
    setAnalyzingPatient(true);
    setSelectedPatient(patientId);
    try {
      const token = localStorage.getItem('hospice_token');
      
      const response = await fetch(`${API_URL}/api/clinical-features/ai-agent/analyze`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          patient_id: patientId,
          analysis_type: 'comprehensive',
          include_recommendations: true
        })
      });
      
      if (response.ok) {
        const analysis = await response.json();
        setPatientAnalysis(analysis);
        toast.success('Analysis Complete', { description: 'AI analysis generated successfully' });
      }
    } catch (error) {
      console.error('Analysis failed:', error);
      toast.error('Failed to analyze patient');
    } finally {
      setAnalyzingPatient(false);
    }
  };
  
  const getAcuityColor = (level) => {
    switch (level) {
      case 'critical': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'moderate': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };
  
  const getTrendIcon = (trend) => {
    if (trend === 'declining') return <TrendingDown className="h-4 w-4 text-red-500" />;
    if (trend === 'improving') return <TrendingUp className="h-4 w-4 text-green-500" />;
    return <Activity className="h-4 w-4 text-gray-500" />;
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
        <span className="ml-2">Loading AI Dashboard...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clinical-ai-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <Brain className="h-5 w-5 text-purple-500" />
            Clinical AI Agent
          </h2>
          <p className="text-slate-500 text-sm">
            AI-powered patient analysis and acuity trending
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" onClick={loadDashboard}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* AI Status Card */}
      {aiStatus && (
        <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-800 rounded-lg">
                  <Brain className="h-6 w-6 text-purple-600 dark:text-purple-300" />
                </div>
                <div>
                  <p className="font-semibold text-purple-900 dark:text-purple-100">
                    AI Agent Status: <span className="text-green-600">{aiStatus.status?.toUpperCase()}</span>
                  </p>
                  <p className="text-sm text-purple-700 dark:text-purple-300">
                    Model: {aiStatus.ai_model} | Version: {aiStatus.version}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1">
                {aiStatus.capabilities?.slice(0, 4).map((cap, i) => (
                  <Badge key={i} variant="outline" className="text-xs">
                    {cap.replace('_', ' ')}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Acuity Summary Cards */}
      {acuityTrends?.summary && (
        <div className="grid grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-slate-500">Total Patients</p>
                  <p className="text-2xl font-bold">{acuityTrends.summary.total_patients}</p>
                </div>
                <Users className="h-8 w-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-red-50 dark:bg-red-900/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-red-600">Critical</p>
                  <p className="text-2xl font-bold text-red-600">{acuityTrends.summary.critical_count}</p>
                </div>
                <AlertOctagon className="h-8 w-8 text-red-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-orange-50 dark:bg-orange-900/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-orange-600">High Acuity</p>
                  <p className="text-2xl font-bold text-orange-600">{acuityTrends.summary.high_count}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-orange-400" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-yellow-50 dark:bg-yellow-900/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-yellow-700">Declining</p>
                  <p className="text-2xl font-bold text-yellow-700">{acuityTrends.summary.declining_count}</p>
                </div>
                <TrendingDown className="h-8 w-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-green-50 dark:bg-green-900/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-green-600">Improving</p>
                  <p className="text-2xl font-bold text-green-600">{acuityTrends.summary.improving_count}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Alerts */}
      {acuityTrends?.alerts?.length > 0 && (
        <div className="space-y-2">
          {acuityTrends.alerts.map((alert, i) => (
            <Alert 
              key={i} 
              className={`${
                alert.type === 'critical' ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'
              }`}
            >
              <AlertTriangle className={`h-4 w-4 ${
                alert.type === 'critical' ? 'text-red-600' : 'text-yellow-600'
              }`} />
              <AlertDescription className={`${
                alert.type === 'critical' ? 'text-red-800' : 'text-yellow-800'
              }`}>
                {alert.message}
              </AlertDescription>
            </Alert>
          ))}
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-3 gap-6">
        {/* Patient Acuity List */}
        <Card className="col-span-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-teal-500" />
              Patient Acuity Ranking
            </CardTitle>
            <CardDescription>
              Click a patient to run AI analysis
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              <div className="space-y-2">
                {acuityTrends?.all_patients?.map((patient, index) => (
                  <div
                    key={patient.patient_id}
                    className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                      selectedPatient === patient.patient_id 
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' 
                        : 'hover:border-slate-300'
                    }`}
                    onClick={() => analyzePatient(patient.patient_id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="text-lg font-bold text-slate-400 w-6">
                          #{index + 1}
                        </div>
                        <div className={`w-3 h-3 rounded-full ${getAcuityColor(patient.acuity_level)}`} />
                        <div>
                          <p className="font-medium">{patient.patient_name || patient.patient_id}</p>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <span>Acuity: {patient.acuity_score}</span>
                            <span>PPS: {patient.pps_score}%</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {getTrendIcon(patient.trend)}
                        <Badge className={getAcuityColor(patient.acuity_level)}>
                          {patient.acuity_level?.toUpperCase()}
                        </Badge>
                        {analyzingPatient && selectedPatient === patient.patient_id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-slate-400" />
                        )}
                      </div>
                    </div>
                    
                    {/* Progress bar for acuity */}
                    <div className="mt-2">
                      <Progress 
                        value={patient.acuity_score} 
                        className="h-1"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* AI Analysis Panel */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Zap className="h-5 w-5 text-yellow-500" />
              AI Analysis
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!patientAnalysis ? (
              <div className="text-center py-12 text-slate-500">
                <Brain className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>Select a patient to view AI analysis</p>
              </div>
            ) : (
              <ScrollArea className="h-[350px]">
                <div className="space-y-4">
                  {/* Patient Info */}
                  <div>
                    <p className="font-semibold">{patientAnalysis.patient_name}</p>
                    <p className="text-xs text-slate-500">
                      Generated: {new Date(patientAnalysis.generated_at).toLocaleString()}
                    </p>
                  </div>
                  
                  {/* Risk Assessment */}
                  <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
                    <p className="font-medium text-sm mb-2">Risk Assessment</p>
                    <div className="flex items-center gap-2">
                      <Badge className={getAcuityColor(patientAnalysis.risk_assessment?.overall_risk)}>
                        {patientAnalysis.risk_assessment?.overall_risk?.toUpperCase()}
                      </Badge>
                      <span className="text-sm">Score: {patientAnalysis.risk_assessment?.score}</span>
                    </div>
                    {patientAnalysis.risk_assessment?.factors?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-slate-500">Risk Factors:</p>
                        <ul className="text-xs list-disc list-inside">
                          {patientAnalysis.risk_assessment.factors.map((f, i) => (
                            <li key={i}>{f}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  
                  {/* Decline Prediction */}
                  {patientAnalysis.decline_prediction && (
                    <div className="p-3 rounded-lg bg-orange-50 dark:bg-orange-900/20">
                      <p className="font-medium text-sm mb-2">Decline Prediction</p>
                      <p className="text-sm">
                        Probability: {(patientAnalysis.decline_prediction.probability * 100).toFixed(0)}%
                      </p>
                      <p className="text-xs text-slate-500">
                        Timeframe: {patientAnalysis.decline_prediction.timeframe_days} days
                      </p>
                    </div>
                  )}
                  
                  {/* Recommendations */}
                  {patientAnalysis.recommendations?.length > 0 && (
                    <div>
                      <p className="font-medium text-sm mb-2">AI Recommendations</p>
                      <div className="space-y-2">
                        {patientAnalysis.recommendations.map((rec, i) => (
                          <div 
                            key={i}
                            className={`p-2 rounded text-xs ${
                              rec.type === 'urgent' 
                                ? 'bg-red-50 dark:bg-red-900/20' 
                                : 'bg-blue-50 dark:bg-blue-900/20'
                            }`}
                          >
                            <p className="font-medium">{rec.recommendation}</p>
                            <p className="text-slate-500 mt-1">{rec.rationale}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Acuity Distribution Chart (Simple) */}
      {acuityTrends?.distribution && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Acuity Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-4 h-32">
              {Object.entries(acuityTrends.distribution).map(([level, count]) => (
                <div key={level} className="flex-1 flex flex-col items-center">
                  <div 
                    className={`w-full ${getAcuityColor(level)} rounded-t`}
                    style={{ 
                      height: `${Math.max((count / Math.max(...Object.values(acuityTrends.distribution), 1)) * 100, 5)}%`,
                      minHeight: '4px'
                    }}
                  />
                  <p className="text-xs mt-2 font-medium">{count}</p>
                  <p className="text-xs text-slate-500 capitalize">{level}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
